import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../../components/SectionMeta';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import { EmptyState } from '../../../styles/feedback/EmptyState';

// ─── Mapeamentos de exibição ──────────────────────────────────────────────────

const ACTION_COLOR = {
   CREATE: '#2e7d32',
   UPDATE: '#1565c0',
   DELETE: '#b71c1c',
};

const SOURCE_LABEL = {
   USER:   'Usuário',
   IMPORT: 'Importação',
   SYSTEM: 'Sistema',
};

const ITEMS_PER_PAGE = 20;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTimeBR(isoString) {
   if (!isoString) return '—';
   return new Date(isoString).toLocaleString('pt-BR', {
      day:    '2-digit',
      month:  '2-digit',
      year:   'numeric',
      hour:   '2-digit',
      minute: '2-digit',
   });
}

// ─── Subcomponente: um evento da timeline ─────────────────────────────────────

function AuditEventItem({ auditEvent }) {
   const {
      entity_label,
      entity_type,
      action,
      action_label,
      performed_by_user_name,
      source,
      event_date,
      summary_lines,
   } = auditEvent;

   const actionColor = ACTION_COLOR[action] ?? '#555';

   return (
      <div
         style={{
            display:      'flex',
            gap:          '14px',
            padding:      '14px 0',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
         }}
      >
         {/* ── Linha vertical + badge de ação ── */}
         <div
            style={{
               display:        'flex',
               flexDirection:  'column',
               alignItems:     'center',
               gap:            '4px',
               flexShrink:     0,
               width:          '72px',
            }}
         >
            <span
               style={{
                  display:      'inline-block',
                  padding:      '3px 8px',
                  borderRadius: '12px',
                  fontSize:     '11px',
                  fontWeight:   600,
                  color:        '#fff',
                  background:   actionColor,
                  whiteSpace:   'nowrap',
               }}
            >
               {action_label}
            </span>
            <span
               style={{
                  fontSize: '10px',
                  color:    '#aaa',
                  textAlign: 'center',
               }}
            >
               {entity_label}
               {entity_type ? ` (${entity_type})` : ''}
            </span>
         </div>

         {/* ── Conteúdo principal ── */}
         <div style={{ flex: 1, minWidth: 0 }}>
            {/* Cabeçalho: quem fez + quando */}
            <div
               style={{
                  display:        'flex',
                  flexWrap:       'wrap',
                  gap:            '6px',
                  alignItems:     'baseline',
                  marginBottom:   '6px',
               }}
            >
               <span style={{ fontWeight: 600, fontSize: '13px' }}>
                  {performed_by_user_name ?? SOURCE_LABEL[source] ?? source}
               </span>
               <span style={{ fontSize: '11px', color: '#888' }}>
                  {formatDateTimeBR(event_date)}
               </span>
               {source !== 'USER' && (
                  <span
                     style={{
                        fontSize:     '10px',
                        color:        '#999',
                        padding:      '1px 6px',
                        background:   '#f3f4f6',
                        borderRadius: '8px',
                     }}
                  >
                     via {SOURCE_LABEL[source] ?? source}
                  </span>
               )}
            </div>

            {/* Linhas de resumo legíveis */}
            <ul
               style={{
                  margin:     0,
                  padding:    0,
                  listStyle:  'none',
               }}
            >
               {summary_lines.map((summaryLine, lineIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li
                     key={lineIndex}
                     style={{
                        fontSize:     '13px',
                        color:        '#374151',
                        lineHeight:   '1.5',
                        paddingLeft:  '10px',
                        borderLeft:   `2px solid ${actionColor}`,
                        marginBottom: lineIndex < summary_lines.length - 1 ? '4px' : 0,
                     }}
                  >
                     {summaryLine}
                  </li>
               ))}
            </ul>
         </div>
      </div>
   );
}

AuditEventItem.propTypes = {
   auditEvent: PropTypes.shape({
      entity_label:           PropTypes.string.isRequired,
      entity_type:            PropTypes.string,
      action:                 PropTypes.string.isRequired,
      action_label:           PropTypes.string.isRequired,
      performed_by_user_name: PropTypes.string,
      source:                 PropTypes.string.isRequired,
      event_date:             PropTypes.string.isRequired,
      summary_lines:          PropTypes.arrayOf(PropTypes.string).isRequired,
   }).isRequired,
};

// ─── Componente principal ─────────────────────────────────────────────────────

export default function EmployeeAuditHistoryTab({ employeeId }) {
   const [auditEvents, setAuditEvents]     = useState([]);
   const [totalEventCount, setTotalEventCount] = useState(0);
   const [totalPages, setTotalPages]       = useState(1);
   const [currentPage, setCurrentPage]     = useState(1);
   const [isLoading, setIsLoading]         = useState(false);

   const fetchAuditHistory = useCallback(async (pageNumber = 1) => {
      setIsLoading(true);
      try {
         const response = await axios.get(
            `/hr/employees/${employeeId}/audit-history`,
            { params: { page: pageNumber, limit: ITEMS_PER_PAGE } },
         );

         setAuditEvents(response.data?.data?.events ?? []);
         setTotalEventCount(response.data?.meta?.total ?? 0);
         setTotalPages(response.data?.meta?.total_pages ?? 1);
         setCurrentPage(pageNumber);
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao carregar histórico de auditoria.';
         toast.error(errorMessage);
      } finally {
         setIsLoading(false);
      }
   }, [employeeId]);

   useEffect(() => {
      fetchAuditHistory(1);
   }, [fetchAuditHistory]);

   return (
      <Section>
         <SectionHeader>
            <SectionHeaderRow>
               <SectionTitle>Histórico de alterações</SectionTitle>
               <SectionMeta
                  items={[
                     {
                        key:   'total',
                        label: 'Total:',
                        value: totalEventCount,
                     },
                     {
                        key:   'page',
                        label: 'Página',
                        value: `${currentPage}/${totalPages || 1}`,
                     },
                  ]}
               />
            </SectionHeaderRow>
         </SectionHeader>

         <SectionBody>
            {isLoading && (
               <p style={{ fontSize: '13px', color: '#888', padding: '8px 0' }}>
                  Carregando histórico...
               </p>
            )}

            {!isLoading && auditEvents.length > 0 && (
               <>
                  <div>
                     {auditEvents.map((auditEvent) => (
                        <AuditEventItem
                           key={`${auditEvent.table_name}-${auditEvent.id}`}
                           auditEvent={auditEvent}
                        />
                     ))}
                  </div>

                  <PaginationBar
                     currentPage={currentPage}
                     totalPages={totalPages}
                     onPageChange={(page) => fetchAuditHistory(page)}
                     disabled={isLoading}
                  />
               </>
            )}

            {!isLoading && auditEvents.length === 0 && (
               <EmptyState
                  title="Nenhuma alteração registrada"
                  description="As ações realizadas neste funcionário aparecerão aqui."
               />
            )}
         </SectionBody>
      </Section>
   );
}

EmployeeAuditHistoryTab.propTypes = {
   employeeId: PropTypes.string.isRequired,
};
